import { useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, A11y } from 'swiper/modules'
import 'swiper/swiper-bundle.css'
import { sanitizeImageUrl } from '../imageUrl'
import { trackPhoneCallClick } from '../services/tracking'
import type { MultiCentreProps, MCFlight, RTWFlight } from '../Types'

const PHONE = {
  economy: '020 7795 4576',
  business: '020 7084 6503',
}

const SWIPER_BREAKPOINTS = {
  576: { slidesPerView: 1, spaceBetween: 20 },
  768: { slidesPerView: 2, spaceBetween: 20 },
  992: { slidesPerView: 3, spaceBetween: 20 },
  1200: { slidesPerView: 4, spaceBetween: 20 },
  1400: { slidesPerView: 4, spaceBetween: 20 },
}

function MCModal({ flight, onClose }: { flight: MCFlight; onClose: () => void }) {
  const isIE = window?.siteVar?.referer === 'IE'
  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div className="modal d-block" tabIndex={-1}>
        <div className="modal-dialog" onClick={e => e.stopPropagation()}>
          <div className="modal-content bg-white">
            <div className="modal-header d-flex flex-row-reverse p-2">
              <button type="button" className="me-3 close border-0 rounded bg-transparent" onClick={onClose}>
                <i className="material-icons">close</i>
              </button>
            </div>
            <div className="modal-body">
              <div className="row g-3 justify-content-lg-center">
                <div className="col-12 col-xl-5 order-1">
                  <div className="row gx-3">
                    {flight.image && (
                      <div className="col-12">
                        <img src={sanitizeImageUrl(flight.image, { width: 480 })} className="img-fluid w-100 rounded-0" alt="" />
                      </div>
                    )}
                    <div className="col-12 d-none d-xl-block">
                      <img src={sanitizeImageUrl(flight.PACK_MAPLINK, { width: 480 })} className="img-fluid w-100 rounded-0" alt="map" />
                    </div>
                  </div>
                </div>
                <div className="col-12 col-xl-5 order-2 pb-3">
                  <div className="d-flex flex-column justify-content-center h-100">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-md-3 mt-xl-0">
                      <h2 className="fw-bold fs-4 mb-sm-2 mb-md-0 ps-3">{flight.PACK_NAME}</h2>
                      {flight.input_offertype && <span className="badge bg-warning me-md-3">{flight.input_offertype}</span>}
                    </div>
                    {flight.copy1 && (
                      <div className="p-3 bg-white mb-3">
                        <p className="fs-6 mb-0" dangerouslySetInnerHTML={{ __html: flight.copy1 }} />
                      </div>
                    )}
                    {!isIE && (
                      <div className="row mt-3 mt-xl-5">
                        <div className="col d-flex flex-column align-items-center">
                          <p className="m-0">Economy</p>
                          <span className="fs-4 fw-bold mb-3"><span className="text-muted fs-6">from</span> £{flight.pack_RateGBP_E} pp</span>
                          <a href={`tel:${PHONE.economy}`} onClick={trackPhoneCallClick} data-ga4-label="tailormade_travel_worldwide" data-ga4-category="text_link_modal">
                            <button type="button" className="btn-btn-warning border-0 rounded-0 bkg-yellow w-100 m-1 pt-2 pb-2">
                              <span className="d-sm-none"><i className="icon-call me-2" />Call to Book</span>
                              <span className="d-none d-sm-block">BOOK NOW: {PHONE.economy}</span>
                            </button>
                          </a>
                        </div>
                        <div className="col d-flex flex-column align-items-center">
                          <p className="m-0">Business</p>
                          <span className="fs-4 fw-bold mb-3"><span className="text-muted fs-6">from</span> £{flight.pack_RateGBP_B} pp</span>
                          <a href={`tel:${PHONE.business}`} onClick={trackPhoneCallClick} data-ga4-label="first_&_business_class_travel" data-ga4-category="text_link_modal">
                            <button type="button" className="btn-btn-warning border-0 rounded-0 bkg-yellow w-100 m-1 pt-2 pb-2">
                              <span className="d-sm-none"><i className="icon-call me-2" />Call to Book</span>
                              <span className="d-none d-sm-block">BOOK NOW: {PHONE.business}</span>
                            </button>
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button type="button" className="btn btn-dark rounded-0 px-5" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function RTWModal({ flight, onClose }: { flight: RTWFlight; onClose: () => void }) {
  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div className="modal d-block" tabIndex={-1}>
        <div className="modal-dialog" onClick={e => e.stopPropagation()}>
          <div className="modal-content bg-white">
            <div className="modal-header d-flex flex-row-reverse p-2">
              <button type="button" className="btn-close me-3" onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="row g-3 justify-content-lg-center">
                <div className="col-12 col-xl-5 order-1">
                  <div className="col-12 d-none d-md-block">
                    <img src={sanitizeImageUrl(flight.rtw_Image, { width: 480 })} className="img-fluid w-100 rounded-0" alt={flight.adv_dest} />
                  </div>
                </div>
                <div className="col-12 col-xl-5 order-2 pb-3">
                  <div className="d-flex flex-column justify-content-center h-100">
                    <h2 className="fw-bold fs-4 mb-sm-2 mb-md-0">{flight.adv_dest}</h2>
                    <ul className="list-underline">
                      {flight.adv_route && <li><span>Route:</span> <span className="text-strong" dangerouslySetInnerHTML={{ __html: flight.adv_route }} /></li>}
                      <li>
                        <span>Included:</span>&nbsp;
                        {flight.adv_inclusions
                          ? <span dangerouslySetInnerHTML={{ __html: flight.adv_inclusions }} />
                          : <span className="text-strong">All flights, fuel surcharges &amp; pre-paid taxes</span>
                        }
                      </li>
                      {flight.hotels && <li><span>Hotels:</span> <span className="text-strong" dangerouslySetInnerHTML={{ __html: flight.hotels }} /></li>}
                      {flight.rails && <li><span>Rail Journeys Included:</span> <span dangerouslySetInnerHTML={{ __html: flight.rails }} /></li>}
                    </ul>
                    <a href="/flights/around-the-world" className="btn btn-outline-orange rounded-0 btn-lg mx-auto">
                      <span>Find out more</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button type="button" className="btn btn-dark rounded-0 px-5" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default function MultiCentre({ mcFlights, rtwFlights }: MultiCentreProps) {
  const isIE = window?.siteVar?.referer === 'IE'
  const [activeTab, setActiveTab] = useState<'mc' | 'rtw'>(isIE ? 'rtw' : 'mc')
  const [activeModal, setActiveModal] = useState<{ type: 'mc' | 'rtw'; index: number } | null>(null)

  const activeMCFlight = activeModal?.type === 'mc' ? mcFlights[activeModal.index] : null
  const activeRTWFlight = activeModal?.type === 'rtw' ? rtwFlights[activeModal.index] : null

  return (
    <div className="container py-5 px-4 px-md-0">

      <ul className="d-flex flex-wrap justify-content-start nav" role="tablist">
        {!isIE && (
          <li className="nav-item">
            <div
              role="tab"
              className={`p-2 pb-3 p-md-3 pb-md-4 ${activeTab === 'mc' ? 'bg-grey' : 'bg-grey-2'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setActiveTab('mc')}
            >
              Multi Centre Flights
            </div>
          </li>
        )}
        <li className="nav-item">
          <div
            role="tab"
            className={`p-2 pb-3 p-md-3 pb-md-4 me-3 ${activeTab === 'rtw' ? 'bg-grey' : 'bg-grey-2'}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setActiveTab('rtw')}
          >
            Round the World
          </div>
        </li>
      </ul>

      {activeTab === 'mc' && (
        <div className="bg-grey p-3 pb-0">
          <Swiper
            modules={[Navigation, A11y]}
            loop
            slidesPerView={1.2}
            spaceBetween={10}
            breakpoints={SWIPER_BREAKPOINTS}
            navigation={{ nextEl: '#mc_swiperForward', prevEl: '#mc_swiperBack' }}
          >
            {mcFlights.map((flight, i) => (
              <SwiperSlide
                key={i}
                className="card item border-0 bg-0 cursor-pointer"
                onClick={() => setActiveModal({ type: 'mc', index: i })}
              >
                <img
                  className="img-fluid w-100 object-fit-cover"
                  style={{ height: '13rem' }}
                  src={sanitizeImageUrl(flight.PACK_MAPLINK, { width: 480 })}
                  alt={flight.PACK_NAME}
                />
                <div className="bg-white px-3 py-5">
                  <p className="mb-0"><b>{flight.PACK_NAME}</b></p>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="container-fluid d-flex justify-content-center text-dark py-4">
            <div id="mc_swiperBack" className="cursor-pointer d-none d-lg-flex">
              <i className="btn btn-light rounded-circle icon-chevron_left glyph-sm p-3 me-4" />
            </div>
            <div id="mc_swiperForward" className="cursor-pointer d-none d-lg-flex">
              <i className="btn btn-light rounded-circle icon-chevron_right glyph-sm p-3" />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'rtw' && (
        <div className="bg-grey p-3 pb-0">
          <Swiper
            modules={[Navigation, A11y]}
            loop
            slidesPerView={1.2}
            spaceBetween={10}
            breakpoints={SWIPER_BREAKPOINTS}
            navigation={{ nextEl: '#rtw_swiperForward', prevEl: '#rtw_swiperBack' }}
          >
            {rtwFlights.map((flight, i) => (
              <SwiperSlide
                key={i}
                className="card item border-0 bg-0 cursor-pointer"
                onClick={() => setActiveModal({ type: 'rtw', index: i })}
              >
                <img
                  className="img-fluid w-100 object-fit-cover"
                  style={{ height: '13rem' }}
                  src={sanitizeImageUrl(flight.rtw_Image, { width: 480 })}
                  alt={flight.adv_dest}
                />
                <div className="bg-white px-3 py-5">
                  <p className="mb-0"><b>{flight.adv_dest}</b></p>
                  <div className="d-flex justify-content-between">
                    {flight.showEcoPrice && <p className="m-0 fs-7 text-muted">Economy from {flight.currency_symbol}{flight.adv_fare}</p>}
                    {flight.showBusPrice && <p className="m-0 fs-7 text-muted">Business from {flight.currency_symbol}{flight.adv_fare_Bus}</p>}
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="container-fluid d-flex justify-content-center text-dark py-4">
            <div id="rtw_swiperBack" className="cursor-pointer d-none d-lg-flex">
              <i className="btn btn-light rounded-circle icon-chevron_left glyph-sm p-3 me-4" />
            </div>
            <div id="rtw_swiperForward" className="cursor-pointer d-none d-lg-flex">
              <i className="btn btn-light rounded-circle icon-chevron_right glyph-sm p-3" />
            </div>
          </div>
        </div>
      )}

      {activeMCFlight && <MCModal flight={activeMCFlight} onClose={() => setActiveModal(null)} />}
      {activeRTWFlight && <RTWModal flight={activeRTWFlight} onClose={() => setActiveModal(null)} />}
    </div>
  )
}
